import { useSuspenseQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import * as React from 'react';
import { useFormContext, useFormState } from 'react-hook-form';

import {
    type Organization,
    OrganizationFeature,
    type PlainObject,
    type Site,
} from '@gitbook/api-client';
import { isVisitorClaimsSet } from '@gitbook/app-adaptive-content';
import * as analytics from '@gitbook/app-analytics/client';
import { type ExpressionError, ExpressionRuntime } from '@gitbook/expr';
import { PlainCodeBlock } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    ActionableContainer,
    Alert,
    Badge,
    Button,
    Description,
    DialogRoot,
    Disclosure,
    DisclosurePanel,
    DisclosureTrigger,
    FieldGroup,
    Group,
    Heading,
    Link,
    type OverlayTriggerState,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import { useLazyRef } from '@gitbook/util-react-hooks';

import { useSetAnalyticsScreen } from '../../../navigation';
import {
    apiQuery,
    isAIDisabledAction,
    isAIPausedAction,
    type UpgradeAction,
    useCurrentOrganization,
    useGetUpgradeAction,
} from '../../../state';
import { ExpressionEditor } from '../../Expressions';
import {
    AdaptiveContentVisitorSegmentsDialog,
    DEFAULT_SEGMENT_TITLE,
} from '../AdaptiveContentVisitorSegmentsDialog';
import { SiteVisitorClaimsButton } from '../SiteVisitorClaimsButton';
import { SiteVisitorCountryButton } from '../SiteVisitorCountryButton';
import { siteVisitorSegmentSelector, useAdaptiveContentBuilderStateContext } from '../state';
import type { AdaptiveContentBuilderFormValues, AdaptiveContentTargetObjectType } from '../types';
import { useSiteVisitorCountry } from '../useSiteVisitorCountry';
import { useSiteVisitorIsAuthenticated } from '../useSiteVisitorIsAuthenticated';
import { useUserDefinedVisitorSegments } from '../useUserDefinedVisitorSegments';
import {
    AdaptiveContentExpressionTemplateSectionSkeleton,
    AdaptiveContentExpressionTestingSectionSkeleton,
    getTextForTarget,
} from './AdaptiveContentExpressionBuilderSkeleton';

interface AdaptiveContentExpressionBuilderProps {
    target: AdaptiveContentTargetObjectType;
}

/**
 * Section of the AdaptiveContentBuilderDialog to build & test an adaptive content expression.
 */
export function AdaptiveContentExpressionBuilder(props: AdaptiveContentExpressionBuilderProps) {
    const { target } = props;

    const { organizationId, siteId, setSelectedTab } = useAdaptiveContentBuilderStateContext();

    const form = useFormContext<AdaptiveContentBuilderFormValues>();
    const { errors } = useFormState({ control: form.control });
    const expression = form.watch('expression') ?? '';

    const runtime = useLazyRef(() => new ExpressionRuntime()).current;
    const [visitorSegment, setVisitorSegment] = useAtom(siteVisitorSegmentSelector(siteId));

    useSetAnalyticsScreen(
        analytics.createSiteEvent(
            { id: siteId, organization: organizationId },
            {
                screen: 'adaptive_content_expression',
                adaptiveContentObject: target,
            }
        ),
        [siteId, organizationId]
    );

    const onExpressionChange = (expr: string, error: ExpressionError | null) => {
        form.setValue('expression', expr, { shouldDirty: true });

        if (expr.length === 0) {
            form.clearErrors('expression');
            return;
        }

        if (error) {
            form.setError('expression', {
                type: 'manual',
                message: error.message,
            });
        } else {
            form.clearErrors('expression');
        }
    };

    const onInsertExpressionTemplate = (templateExpr: string) => {
        form.setValue('expression', templateExpr, { shouldDirty: true });
    };

    const visitorClaims = visitorSegment?.claims ?? {};
    const evaluationClaims = {
        visitor: {
            type: 'human' as const,
            claims: visitorClaims,
            isSet: isVisitorClaimsSet(visitorClaims),
        },
    };

    const visitorSegmentsDialogTrigger = useDialogTriggerState();

    return (
        <Disclosure defaultExpanded={true}>
            {({ isExpanded }) => (
                <div className="flex flex-1">
                    <div className="group/expression-builder flex flex-1 flex-col">
                        <div className="flex flex-1 flex-col gap-6 overflow-auto px-10 py-6">
                            <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                    <Heading level={2} className="flex-1">
                                        Only show this {getTextForTarget(target)} if:
                                    </Heading>
                                    <DisclosureTrigger>
                                        <Button
                                            kind="blank"
                                            iconOnly
                                            leadingIcon={
                                                isExpanded
                                                    ? Icon.PanelLeftOpen
                                                    : Icon.PanelLeftClose
                                            }
                                            label={isExpanded ? 'Hide examples' : 'Show examples'}
                                            className="opacity-0 group-hover/expression-builder:opacity-100 focus:opacity-100"
                                        />
                                    </DisclosureTrigger>
                                </div>
                                <Description>
                                    Define who can access this {getTextForTarget(target)} by writing
                                    an expression or picking from one of the examples. To use
                                    different properties, edit your{' '}
                                    <Link onPress={() => setSelectedTab('schema')}>
                                        visitor schema
                                    </Link>
                                    .
                                </Description>
                            </div>
                            <div className="rounded-lg border bg-base">
                                <ExpressionEditor
                                    mode="deterministic-expression"
                                    value={expression}
                                    onChange={onExpressionChange}
                                    autoFocus
                                    size="large"
                                    documentationURL={{
                                        path: 'publishing-documentation/adaptive-content',
                                    }}
                                />
                            </div>
                        </div>
                        {!('expression' in errors && errors.expression?.message) ? (
                            <React.Suspense
                                fallback={
                                    <AdaptiveContentExpressionTestingSectionSkeleton
                                        target={target}
                                    />
                                }
                            >
                                <AdaptiveContentExpressionTestingSection
                                    target={target}
                                    organizationId={organizationId}
                                    siteId={siteId}
                                    expression={expression}
                                    runtime={runtime}
                                    evaluatedClaims={evaluationClaims}
                                    visitorSegmentsDialogTrigger={visitorSegmentsDialogTrigger}
                                />
                                <DialogRoot {...visitorSegmentsDialogTrigger.rootProps}>
                                    <AdaptiveContentVisitorSegmentsDialog
                                        organizationId={organizationId}
                                        siteId={siteId}
                                        onConfirm={(selectedSegment) =>
                                            setVisitorSegment(selectedSegment)
                                        }
                                        selectedSegment={visitorSegment ?? undefined}
                                    />
                                </DialogRoot>
                            </React.Suspense>
                        ) : null}
                    </div>
                    <DisclosurePanel
                        orientation="horizontal"
                        className="h-full max-w-[50%] overflow-y-auto bg-side"
                    >
                        <React.Suspense
                            fallback={<AdaptiveContentExpressionTemplateSectionSkeleton />}
                        >
                            <ActionableContainer className="px-10 py-6">
                                <div className="flex items-center">
                                    <Heading>Examples</Heading>
                                    <Description className="ml-auto">
                                        Based on your{' '}
                                        <Link onPress={() => setSelectedTab('schema')}>
                                            visitor schema
                                        </Link>
                                        <Icon.ChevronRight />
                                    </Description>
                                </div>
                                <AdaptiveContentExpressionTemplatesSection
                                    organizationId={organizationId}
                                    siteId={siteId}
                                    onInsertExpressionTemplate={onInsertExpressionTemplate}
                                />
                            </ActionableContainer>
                        </React.Suspense>
                    </DisclosurePanel>
                </div>
            )}
        </Disclosure>
    );
}

/**
 * Section of the adaptive content expression builder showing example/template expressions generated based on site schema.
 */
function AdaptiveContentExpressionTemplatesSection(props: {
    organizationId: Organization['id'];
    siteId: Site['id'];
    onInsertExpressionTemplate: (templateExpr: string) => void;
}) {
    const organization = useCurrentOrganization();
    const upgrade = useGetUpgradeAction({ organization, featureKey: OrganizationFeature.Ai });

    // As the API, only an administrator turning AI off stops the examples: they are Platform AI
    // work, so orgs whose plan lacks AI still see them.
    if (isAIDisabledAction(upgrade)) {
        return (
            <Alert
                kind="info"
                size="small"
                icon={Icon.AI}
                details="Examples are written by GitBook AI, which is disabled for this organization."
                action={{
                    leadingIcon: upgrade.leadingIcon ?? Icon.AI,
                    label: upgrade.label,
                    onPress: upgrade.onPress,
                }}
            />
        );
    }

    return <AdaptiveContentExpressionTemplatesList {...props} upgrade={upgrade} />;
}

function AdaptiveContentExpressionTemplatesList(props: {
    organizationId: Organization['id'];
    siteId: Site['id'];
    onInsertExpressionTemplate: (templateExpr: string) => void;
    upgrade: UpgradeAction | undefined;
}) {
    const { organizationId, siteId, onInsertExpressionTemplate, upgrade } = props;

    const { data: templateConditions } = useSuspenseQuery(
        apiQuery('listSiteAdaptiveTemplateConditions', {
            organizationId,
            siteId,
        })
    );

    // Where the plan lacks AI, the upgrade requirement hides an administrator turning AI off, and
    // the API then returns no examples. Offer the upgrade rather than an empty list.
    if (templateConditions.items.length === 0 && upgrade && !isAIPausedAction(upgrade)) {
        return (
            <Alert
                kind="upgrade"
                size="small"
                icon={Icon.Upgrade}
                details="Examples are written by GitBook AI, which isn't available on your plan."
                action={{
                    leadingIcon: upgrade.leadingIcon ?? Icon.Upgrade,
                    label: upgrade.label,
                    onPress: upgrade.onPress,
                }}
            />
        );
    }

    return (
        <div className="flex1 flex flex-col gap-4">
            {templateConditions.items.map((template) => (
                <div key={template.condition} className="flex flex-col gap-2">
                    <Description>{template.description}</Description>
                    <PlainCodeBlock
                        syntax="javascript"
                        code={template.condition}
                        toolbar={
                            <Button
                                kind="secondary"
                                size="small"
                                leadingIcon={Icon.Replace}
                                label="Use"
                                onPress={() => onInsertExpressionTemplate(template.condition)}
                                className="opacity-0 group-hover/template:opacity-100 focus:opacity-100"
                            />
                        }
                        showToolbar={true}
                        showCopyButton={false}
                        overflow="wrap"
                        className="group/template [&&]:bg-base"
                    />
                </div>
            ))}
        </div>
    );
}

/**
 * Section of the adaptive content expression builder where the outcome of the expression evaluation is shown.
 */
function AdaptiveContentExpressionTestingSection(props: {
    target: AdaptiveContentTargetObjectType;
    organizationId: Organization['id'];
    siteId: Site['id'];
    expression: string;
    runtime: ExpressionRuntime;
    evaluatedClaims: {
        visitor: { type: 'human' | 'agent'; claims: PlainObject; isSet: boolean };
    };
    visitorSegmentsDialogTrigger: OverlayTriggerState;
}) {
    const {
        target,
        expression,
        runtime,
        evaluatedClaims,
        organizationId,
        siteId,
        visitorSegmentsDialogTrigger,
    } = props;

    const [visitorSegment, setVisitorSegment] = useAtom(siteVisitorSegmentSelector(siteId));
    const { isAuthenticated } = useSiteVisitorIsAuthenticated({ organizationId, siteId });
    const [segmentClaimsShowing, setSegmentClaimsShowing] = React.useState(false);
    const { region } = useSiteVisitorCountry({ organizationId, siteId });

    const { userDefinedSegments, addVisitorSegment } = useUserDefinedVisitorSegments();
    const { data: autoDetectedSegments } = useSuspenseQuery(
        apiQuery('listSiteVisitorSegments', {
            organizationId,
            siteId,
        })
    );

    if (!userDefinedSegments?.length && !autoDetectedSegments?.items.length) {
        return (
            <div className="flex border-t px-4 py-3">
                <FieldGroup orientation="horizontal">
                    <Group>
                        <Description>
                            You can test a condition’s outcome by comparing it against a snippet of
                            JSON.
                        </Description>
                    </Group>
                    <Link
                        onPress={async () => {
                            const addedSegment = await addVisitorSegment({
                                title: DEFAULT_SEGMENT_TITLE,
                                claims: {},
                            });
                            setVisitorSegment({ type: 'user-defined', ...addedSegment });
                            visitorSegmentsDialogTrigger.open();
                        }}
                    >
                        Add a JSON snippet
                    </Link>
                    <DialogRoot {...visitorSegmentsDialogTrigger.rootProps}>
                        <AdaptiveContentVisitorSegmentsDialog
                            organizationId={organizationId}
                            siteId={siteId}
                            onConfirm={(selectedSegment) => setVisitorSegment(selectedSegment)}
                            selectedSegment={visitorSegment ?? undefined}
                        />
                    </DialogRoot>
                </FieldGroup>
            </div>
        );
    }

    const testContext = {
        ...evaluatedClaims,
        visitor: { ...evaluatedClaims.visitor, region, isAuthenticated },
    };
    const evaluationOutcome = runtime.evaluateBoolean(expression, testContext);

    return (
        <div className="flex flex-col border-t">
            <div className="flex flex-col gap-3 px-4 py-3">
                <div className="flex items-center">
                    <SiteVisitorClaimsButton
                        size="small"
                        organizationId={organizationId}
                        siteId={siteId}
                    />
                    <SiteVisitorCountryButton
                        className="ml-2"
                        organizationId={organizationId}
                        siteId={siteId}
                    />
                    {visitorSegment && (
                        <Button
                            kind="blank"
                            className="ml-auto"
                            label={`${segmentClaimsShowing ? 'Hide' : 'View'} test data`}
                            leadingIcon={Icon.CodeObject}
                            onPress={() => setSegmentClaimsShowing(!segmentClaimsShowing)}
                        >
                            {segmentClaimsShowing ? 'Hide' : 'View'} test data
                        </Button>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <Badge label={evaluationOutcome ? 'TRUE' : 'FALSE'} />
                    <Description>
                        This {target} <b>{evaluationOutcome ? 'would' : "wouldn't"}</b> be
                        accessible to this test user.
                    </Description>
                </div>
            </div>
            {visitorSegment && segmentClaimsShowing ? (
                <div className="-mb-[0.5em] max-h-64 overflow-y-auto border-t">
                    <PlainCodeBlock
                        code={JSON.stringify(testContext, null, 2)}
                        syntax="json"
                        lineNumbers
                        overflow="wrap"
                    />
                </div>
            ) : null}
        </div>
    );
}

export function AdaptiveContentExpressionBuilderReadOnly(props: {
    expression: string;
    target: AdaptiveContentTargetObjectType;
}) {
    const { expression, target } = props;
    return (
        <div className="flex flex-col gap-4">
            <Heading level={2}>Only show this {target} if:</Heading>
            <Description>
                This expression defines who can access this page. You can edit it in a change
                request.
            </Description>
            <ExpressionEditor value={expression} onChange={() => {}} readOnly size="large" />
        </div>
    );
}
